import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RotateCcw, Send, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { publicAsset } from "@/lib/publicAsset";
import { supabase } from "@/lib/supabaseClient";
import { userDisplayName } from "@/lib/userDisplay";
import LazyCatalogImage from "@/components/vip-simulator/LazyCatalogImage";
import { detectItems } from "./zaroItems";
import "./ZaroChat.css";

const ZARO_ICON = publicAsset("/images/zaro/zaro.webp");
const STORAGE_PREFIX = "zrs_zaro_chat_v1:";
const MAX_CHARS = 800;
const MAX_STORED = 40;
/** Só estes links ficam clicáveis nas respostas (o resto aparece como texto). */
const SAFE_LINK = /^(https:\/\/(www\.)?zrserver\.online(\/[^\s]*)?|https:\/\/discord\.gg\/QK8N5xPNAC)$/;

/** Histórico guardado por conta (não passa de uma conta para outra no mesmo browser). */
function loadState(userId) {
    if (!userId) return { conversationId: null, messages: [] };
    try {
        const s = JSON.parse(localStorage.getItem(STORAGE_PREFIX + userId) || "null");
        if (s && Array.isArray(s.messages)) return s;
    } catch {
        /* ignore */
    }
    return { conversationId: null, messages: [] };
}

function saveState(userId, state) {
    if (!userId) return;
    try {
        localStorage.setItem(
            STORAGE_PREFIX + userId,
            JSON.stringify({ conversationId: state.conversationId, messages: state.messages.slice(-MAX_STORED) }),
        );
    } catch {
        /* ignore */
    }
}

/** "**frase com **negrito** dentro**" (o modelo às vezes aninha) → "**frase com negrito dentro**". */
function flattenNestedBold(text) {
    const t = String(text).trim();
    if (t.startsWith("**") && /\*\*[.!?:]?$/.test(t) && (t.match(/\*\*/g) || []).length > 2) {
        const end = t.match(/\*\*([.!?:]?)$/);
        return `**${t.slice(2, t.length - end[0].length).replace(/\*\*/g, "")}${end[1]}**`;
    }
    return text;
}

/** **negrito** e links permitidos, sem HTML. */
function renderInline(raw, keyBase) {
    const text = flattenNestedBold(raw);
    const out = [];
    const re = /(\*\*[^*]+\*\*|`[^`\n]+`|https?:\/\/[^\s)`]+)/g;
    let last = 0;
    let m;
    let i = 0;
    while ((m = re.exec(text))) {
        if (m.index > last) out.push(text.slice(last, m.index));
        const tok = m[0];
        if (tok.startsWith("**")) {
            out.push(<strong key={`${keyBase}-b${i++}`}>{tok.slice(2, -2)}</strong>);
        } else if (tok.startsWith("`")) {
            out.push(<code key={`${keyBase}-c${i++}`}>{tok.slice(1, -1)}</code>);
        } else {
            const url = tok.replace(/[.,;:!?]+$/, "");
            const trail = tok.slice(url.length);
            out.push(
                SAFE_LINK.test(url) ? (
                    <a key={`${keyBase}-a${i++}`} href={url} target="_blank" rel="noreferrer noopener">
                        {url.replace(/^https:\/\/(www\.)?/, "")}
                    </a>
                ) : (
                    url
                ),
            );
            if (trail) out.push(trail);
        }
        last = m.index + tok.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

/** Markdown mínimo: parágrafos, listas "- " / "1." e **negrito**. */
function RichText({ text }) {
    const blocks = [];
    let list = null;
    String(text)
        .split("\n")
        .forEach((raw, idx) => {
            const line = raw.trim();
            const bullet = line.match(/^(?:[-*•]|\d+[.)])\s+(.*)$/);
            if (bullet) {
                if (!list) {
                    list = { ordered: /^\d/.test(line), items: [] };
                    blocks.push(list);
                }
                list.items.push(bullet[1]);
                return;
            }
            list = null;
            if (line) blocks.push({ p: line.replace(/^#+\s*/, ""), idx });
        });
    return blocks.map((b, i) => {
        if (b.items) {
            const Tag = b.ordered ? "ol" : "ul";
            return (
                <Tag key={i}>
                    {b.items.map((it, j) => (
                        <li key={j}>{renderInline(it, `${i}-${j}`)}</li>
                    ))}
                </Tag>
            );
        }
        return <p key={i}>{renderInline(b.p, `${i}`)}</p>;
    });
}

function ItemStrip({ text, label }) {
    const { item } = useI18n();
    const items = useMemo(() => detectItems(text), [text]);
    if (!items.length) return null;
    return (
        <div className="zaro-items" aria-label={label}>
            {items.map((it) => (
                <span key={it.id} className="zaro-item" title={item(it.name)}>
                    <LazyCatalogImage
                        src={it.icon}
                        fallbacks={it.iconCandidates}
                        alt=""
                        className="zaro-item__img"
                        eager
                    />
                    <span className="zaro-item__name">{item(it.name)}</span>
                </span>
            ))}
        </div>
    );
}

/**
 * @param {{ user: import('@supabase/supabase-js').User | null, onLoginClick: () => void }} props
 */
export default function ZaroChat({ user, onLoginClick }) {
    const { t, lang } = useI18n();
    const userId = user?.id ?? null;
    const playerName = user ? userDisplayName(user) : null;
    const [open, setOpen] = useState(false);
    const [state, setState] = useState(() => loadState(userId));
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const listRef = useRef(null);
    const inputRef = useRef(null);
    const lastFailedRef = useRef(null);

    // Troca de conta (login/logout) → carrega o histórico dessa conta.
    useEffect(() => {
        setState(loadState(userId));
        setError(null);
        lastFailedRef.current = null;
    }, [userId]);

    useEffect(() => saveState(userId, state), [userId, state]);

    useEffect(() => {
        if (!open) return undefined;
        inputRef.current?.focus();
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open]);

    useEffect(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [state.messages, sending, open, error]);

    const send = useCallback(
        async (raw) => {
            const text = String(raw || "").trim().slice(0, MAX_CHARS);
            if (!text || sending || !userId) return;
            setError(null);
            setInput("");
            const history = state.messages.slice(-12);
            setState((s) => ({ ...s, messages: [...s.messages, { role: "user", content: text }] }));
            setSending(true);
            try {
                const headers = { "Content-Type": "application/json" };
                const token = (await supabase?.auth.getSession())?.data?.session?.access_token;
                if (token) headers.Authorization = `Bearer ${token}`;
                const res = await fetch("/api/zaro/chat", {
                    method: "POST",
                    headers,
                    body: JSON.stringify({ message: text, conversationId: state.conversationId, lang, history }),
                });
                const json = await res.json().catch(() => ({}));
                if (!res.ok || typeof json.reply !== "string") {
                    const code = json.error;
                    lastFailedRef.current = text;
                    if (res.status === 401) {
                        lastFailedRef.current = null;
                        setError(t("zaro.error.login"));
                    } else if (res.status === 429 && code === "rate_limited") {
                        setError(t("zaro.error.rate", { seconds: json.retryAfter || 60 }));
                    } else if (res.status === 429) setError(t("zaro.error.busy"));
                    else if (code === "not_configured" || res.status === 404) setError(t("zaro.error.offline"));
                    else if (code === "conversation_full") setError(t("zaro.error.full"));
                    else if (code === "banned") setError(t("zaro.error.banned"));
                    else setError(t("zaro.error.generic"));
                    return;
                }
                lastFailedRef.current = null;
                setState((s) => ({
                    conversationId: json.conversationId || s.conversationId,
                    messages: [...s.messages, { role: "assistant", content: json.reply }],
                }));
            } catch {
                lastFailedRef.current = text;
                setError(t("zaro.error.generic"));
            } finally {
                setSending(false);
            }
        },
        [sending, userId, state.conversationId, state.messages, lang, t],
    );

    const retry = () => {
        const text = lastFailedRef.current;
        if (!text) return;
        // Remove a pergunta que falhou para não ficar duplicada.
        setState((s) => {
            const msgs = [...s.messages];
            if (msgs.length && msgs[msgs.length - 1].role === "user") msgs.pop();
            return { ...s, messages: msgs };
        });
        send(text);
    };

    const newChat = () => {
        setState({ conversationId: null, messages: [] });
        setError(null);
        lastFailedRef.current = null;
        inputRef.current?.focus();
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            send(input);
        }
    };

    const suggestions = t("zaro.suggestions");
    const charsLeft = MAX_CHARS - input.length;

    return (
        <>
            {!open && (
                <button
                    type="button"
                    className="zaro-launcher"
                    onClick={() => setOpen(true)}
                    aria-label={t("zaro.launcher")}
                    title={t("zaro.launcher")}
                    data-testid="zaro-launcher"
                >
                    <img src={ZARO_ICON} alt="" className="zaro-launcher__img" draggable={false} />
                    <span className="zaro-launcher__ping" aria-hidden />
                </button>
            )}

            {open && (
                <section
                    className="zaro-panel"
                    role="dialog"
                    aria-modal="false"
                    aria-label={t("zaro.name")}
                    data-testid="zaro-panel"
                >
                    <header className="zaro-head">
                        <img src={ZARO_ICON} alt="" className="zaro-head__avatar" />
                        <div className="zaro-head__text">
                            <p className="zaro-head__name">{t("zaro.name")}</p>
                            <p className="zaro-head__status">
                                <span className="zaro-dot" aria-hidden />
                                {t("zaro.status")}
                            </p>
                        </div>
                        <button type="button" className="zaro-icon-btn" onClick={newChat} title={t("zaro.newChat")} aria-label={t("zaro.newChat")}>
                            <RotateCcw size={16} />
                        </button>
                        <button type="button" className="zaro-icon-btn" onClick={() => setOpen(false)} title={t("zaro.close")} aria-label={t("zaro.close")}>
                            <X size={18} />
                        </button>
                    </header>

                    <div className="zaro-list" ref={listRef} aria-live="polite">
                        <div className="zaro-msg zaro-msg--bot">
                            <img src={ZARO_ICON} alt="" className="zaro-msg__avatar" />
                            <div className="zaro-bubble">
                                <RichText
                                    text={user ? t("zaro.welcomeUser", { name: playerName }) : t("zaro.welcome")}
                                />
                                {!user && (
                                    <button type="button" className="zaro-login" onClick={onLoginClick} data-testid="zaro-login-btn">
                                        {t("zaro.loginCta")}
                                    </button>
                                )}
                            </div>
                        </div>

                        {user && state.messages.length === 0 && Array.isArray(suggestions) && (
                            <div className="zaro-suggestions" aria-label={t("zaro.suggestionsLabel")}>
                                {suggestions.map((s) => (
                                    <button key={s} type="button" className="zaro-chip" onClick={() => send(s)}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}

                        {state.messages.map((m, i) =>
                            m.role === "user" ? (
                                <div key={i} className="zaro-msg zaro-msg--user">
                                    <div className="zaro-bubble">{m.content}</div>
                                </div>
                            ) : (
                                <div key={i} className="zaro-msg zaro-msg--bot">
                                    <img src={ZARO_ICON} alt="" className="zaro-msg__avatar" />
                                    <div className="zaro-bubble">
                                        <RichText text={m.content} />
                                        <ItemStrip text={m.content} label={t("zaro.itemsLabel")} />
                                    </div>
                                </div>
                            ),
                        )}

                        {sending && (
                            <div className="zaro-msg zaro-msg--bot" aria-label={t("zaro.typing")}>
                                <img src={ZARO_ICON} alt="" className="zaro-msg__avatar" />
                                <div className="zaro-bubble zaro-typing">
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="zaro-error" role="alert">
                                <p>{error}</p>
                                {lastFailedRef.current && (
                                    <button type="button" className="zaro-chip" onClick={retry}>
                                        {t("zaro.retry")}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <form
                        className="zaro-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            send(input);
                        }}
                    >
                        <textarea
                            ref={inputRef}
                            className="zaro-input"
                            rows={1}
                            value={input}
                            maxLength={MAX_CHARS}
                            placeholder={user ? t("zaro.placeholder") : t("zaro.placeholderLocked")}
                            disabled={!user}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={onKeyDown}
                            aria-label={t("zaro.placeholder")}
                            data-testid="zaro-input"
                        />
                        <button type="submit" className="zaro-send" disabled={!user || !input.trim() || sending} aria-label={t("zaro.send")} data-testid="zaro-send">
                            <Send size={17} />
                        </button>
                    </form>
                    {charsLeft < 150 && <p className="zaro-counter">{t("zaro.charsLeft", { n: charsLeft })}</p>}
                    <p className="zaro-disclaimer">{t("zaro.disclaimer")}</p>
                </section>
            )}
        </>
    );
}
